import { useState } from "react";
import {
  Users,
  GraduationCap,
  Wallet,
  TrendingUp,
  Trophy,
  Info,
  Layers,
  Code2,
  Calculator,
  HeartPulse,
  PiggyBank,
  Sparkles,
  Salad,
  Dumbbell,
  Moon,
  Brain,
  Check,
  X,
  RotateCcw,
  Target,
  UserX,
} from "lucide-react";
import { OptionSelector } from "@/components/OptionSelector";
import { cn } from "@/lib/utils";
import {
  calcular,
  calcularAhorro,
  dinero,
  numero,
  numeroDec,
  MODULOS,
  MAX_MODULOS_QUITADOS,
  OPCIONES_ESTUDIANTES,
  SELECCION_DEFAULT,
  USUARIOS_RRHH,
} from "@/lib/pricing";

const INDIGO = "#24255b";
const GOLD = "#ffc629";
const BLUE = "#4070b6";

const MODULO_ICONOS = {
  nutricion: Salad,
  fisica: Dumbbell,
  descanso: Moon,
  mental: Brain,
};

export const Calculadora = ({ config }) => {
  const [seleccion, setSeleccion] = useState(SELECCION_DEFAULT);
  const soloRRHH = seleccion.estudiantes === 0;
  const r = calcular(config, seleccion);

  const set = (patch) => setSeleccion((s) => ({ ...s, ...patch }));

  return (
    <div>
      {/* Resumen siempre visible */}
      <div
        className="sticky top-3 z-30 mb-6 rounded-2xl px-4 sm:px-6 py-3 text-white flex items-center justify-between gap-3 flex-wrap"
        style={{ backgroundColor: INDIGO, boxShadow: "0 8px 30px rgba(36,37,91,.25)" }}
        data-testid="resumen-sticky"
      >
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[11px] uppercase tracking-wider text-[#b8bae0] font-semibold">
            Prom./usuario
          </span>
          <span
            className="font-extrabold tabular-nums text-xl sm:text-2xl whitespace-nowrap"
            style={{ color: GOLD }}
            data-testid="resumen-sticky-prom"
          >
            {dinero(r.precioPromedioPorUsuario)}
          </span>
        </div>
        <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm">
          <span className="whitespace-nowrap">
            <span className="text-[#b8bae0]">Total mensual </span>
            <span className="font-bold tabular-nums" data-testid="resumen-sticky-total">
              {dinero(r.totalMensual)}
            </span>
          </span>
          <span className="whitespace-nowrap hidden sm:inline">
            <span className="text-[#b8bae0]">Usuarios </span>
            <span className="font-bold tabular-nums">{numero(r.usuariosTotales)}</span>
          </span>
          <span className="whitespace-nowrap hidden md:inline">
            <span className="text-[#b8bae0]">Estudiantes </span>
            <span className="font-bold tabular-nums">{numero(r.estudiantes)}</span>
          </span>
        </div>
      </div>

    <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-6 lg:gap-8 items-start">
      {/* Columna izquierda: configuración + ahorro */}
      <div className="min-w-0 space-y-6">
      {/* Panel de selección */}
      <div
        className="bg-white rounded-2xl border border-[#e6e7f0] p-6 sm:p-7 space-y-6"
        style={{ boxShadow: "0 8px 30px rgba(36,37,91,.06)" }}
      >
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#ff5566]">
            Configura tu plan
          </p>
          <h2 className="text-lg font-bold text-[#24255b] mt-1">Opciones del proyecto</h2>
          <p className="text-sm text-[#6c6d85] mt-1">
            Ajusta el alcance y observa la inversión mensual en tiempo real. Ningún cambio
            modifica los precios base de la propuesta.
          </p>
        </div>

        {/* Gamificación siempre incluida */}
        <NotaGamificacion />

        {/* Horas de desarrollo incluidas */}
        <NotaDesarrollo />

        <div className="space-y-2">
          <OptionSelector
            label="Estudiantes a incluir"
            testId="selector-estudiantes"
            options={OPCIONES_ESTUDIANTES}
            value={seleccion.estudiantes}
            onChange={(v) => set({ estudiantes: v })}
          />
          <NotaMini>
            Sobre los 1,200 usuarios de RRHH ya contemplados. Elige cuántos estudiantes de
            la comunidad se suman al proyecto.
          </NotaMini>
        </div>

        <div className="space-y-2">
          <ModulosSelector
            quitados={soloRRHH ? [] : seleccion.modulosQuitados}
            onChange={(next) => set({ modulosQuitados: next })}
            disabled={soloRRHH}
          />
        </div>

        {/* Nota: mínimo de funcionalidades */}
        <NotaMinimo />

        {soloRRHH && (
          <p
            className="text-xs text-[#6c6d85] bg-[#f4f5fa] rounded-lg px-3 py-2.5 border border-[#e6e7f0]"
            data-testid="nota-solo-rrhh"
          >
            Con "Solo RRHH" no se incluyen estudiantes nuevos, por lo que la salud mental y
            los módulos se mantienen completos y no se aplican descuentos.
          </p>
        )}
      </div>

      {/* Calculadora de ahorro / ROI */}
      <RoiAhorro config={config} r={r} />
      </div>

      {/* Panel de resultados */}
      <div className="min-w-0 space-y-6" data-testid="resultados">
        {/* Tarjetas resumen */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            icon={<Wallet className="w-5 h-5" />}
            label="Total mensual"
            value={dinero(r.totalMensual)}
            nota="Inversión mensual total del proyecto."
            testId="card-total-mensual"
          />
          <SummaryCard
            highlight
            icon={<TrendingUp className="w-5 h-5" />}
            label="Precio prom. / usuario"
            value={dinero(r.precioPromedioPorUsuario)}
            nota="Total mensual dividido entre todos los usuarios."
            testId="card-precio-promedio"
          />
          <SummaryCard
            icon={<Users className="w-5 h-5" />}
            label="Usuarios totales"
            value={numero(r.usuariosTotales)}
            nota="Estudiantes + administrativos (RRHH)."
            testId="card-usuarios-totales"
          />
          <SummaryCard
            icon={<GraduationCap className="w-5 h-5" />}
            label="Estudiantes"
            value={numero(r.estudiantes)}
            nota="Estudiantes de la comunidad incluidos."
            testId="card-estudiantes"
          />
        </div>

        {/* Cómo se calcula el precio promedio */}
        <NotaPromedio
          total={r.totalMensual}
          usuarios={r.usuariosTotales}
          promedio={r.precioPromedioPorUsuario}
        />

        {/* Tabla de tramos */}
        <div
          className="bg-white rounded-2xl border border-[#e6e7f0] overflow-hidden"
          style={{ boxShadow: "0 8px 30px rgba(36,37,91,.06)" }}
        >
          <div className="px-6 pt-5 pb-3">
            <h3 className="text-base font-bold text-[#24255b]">Detalle por rango</h3>
            <p className="text-xs text-[#6c6d85] mt-1">
              Precio final por usuario en cada rango, según las opciones elegidas.
            </p>
            <NotaEscalonado />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[440px]" data-testid="tabla-tramos">
              <thead>
                <tr className="text-left text-[#6c6d85] text-xs uppercase tracking-wide border-b border-[#eff0f6]">
                  <th className="px-6 py-2.5 font-semibold">Rango</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Usuarios</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Precio / usuario</th>
                  <th className="px-6 py-2.5 font-semibold text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eff0f6]">
                <TramoRow
                  nombre="Rango Base — RRHH"
                  rango="Usuarios 1 – 1.200"
                  usuarios={USUARIOS_RRHH}
                  precio={r.precioRRHH}
                  subtotal={USUARIOS_RRHH * r.precioRRHH}
                />
                <TramoRow
                  nombre="Rango 1"
                  rango="Usuarios 1.201 – 5.100"
                  usuarios={r.estudiantesEnTramo1}
                  precio={r.precioTramo1Final}
                  subtotal={r.estudiantesEnTramo1 * r.precioTramo1Final}
                  atenuado={r.estudiantesEnTramo1 === 0}
                />
                <TramoRow
                  nombre="Rango 2"
                  rango="Usuarios 5.101 – 9.000"
                  usuarios={r.estudiantesEnTramo2}
                  precio={r.precioTramo2Final}
                  subtotal={r.estudiantesEnTramo2 * r.precioTramo2Final}
                  atenuado={r.estudiantesEnTramo2 === 0}
                />
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#eff0f6]" style={{ backgroundColor: "#fff8e6" }}>
                  <td className="px-6 py-3.5 font-bold text-[#24255b]" colSpan={3}>
                    Total mensual
                  </td>
                  <td
                    className="px-6 py-3.5 text-right font-bold tabular-nums text-[#24255b]"
                    data-testid="tabla-total"
                  >
                    {dinero(r.totalMensual)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {/* Nota economías de escala */}
          <div className="px-6 pb-5 pt-1">
            <NotaEscala />
          </div>
        </div>

        {/* Desglose RRHH vs Estudiantes */}
        <div
          className="bg-white rounded-2xl border border-[#e6e7f0] p-6"
          style={{ boxShadow: "0 8px 30px rgba(36,37,91,.06)" }}
        >
          <h3 className="text-base font-bold text-[#24255b] mb-1">
            Distribución del total mensual
          </h3>
          <p className="text-xs text-[#6c6d85] mb-4">
            Cómo se reparten los <span className="font-semibold">gastos mensuales</span> entre
            RRHH y estudiantes, calculado con la tarifa promedio por usuario del plan (por eso
            ambos montos suman exactamente el total mensual).
          </p>
          <BarraDesglose
            rrhh={r.montoMostradoRRHH}
            estudiantes={r.montoMostradoEstudiantes}
            total={r.totalMensual}
          />
          <div className="grid grid-cols-2 gap-4 mt-5">
            <BreakdownCard
              color={INDIGO}
              label="RRHH"
              usuarios={USUARIOS_RRHH}
              monto={r.montoMostradoRRHH}
              testId="desglose-rrhh"
            />
            <BreakdownCard
              color={BLUE}
              label="Estudiantes"
              usuarios={r.estudiantes}
              monto={r.montoMostradoEstudiantes}
              testId="desglose-estudiantes"
              icon={<GraduationCap className="w-4 h-4" />}
            />
          </div>
        </div>

        {/* Punto de equilibrio */}
        <PuntoEquilibrio config={config} r={r} />
      </div>
    </div>
    </div>
  );
};

/* ---------- Selector de módulos (4 pilares) ---------- */

const ModulosSelector = ({ quitados, onChange, disabled }) => {
  const lleno = quitados.length >= MAX_MODULOS_QUITADOS;

  const toggle = (key) => {
    if (disabled) return;
    if (quitados.includes(key)) {
      onChange(quitados.filter((k) => k !== key));
    } else {
      if (lleno) return;
      onChange([...quitados, key]);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="selector-modulos">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-[#24255b]">Módulos del plan</span>
        <div className="flex items-center gap-2">
          <span
            className="text-[11px] font-medium text-[#6c6d85] bg-[#f4f5fa] px-2 py-0.5 rounded-full"
            data-testid="modulos-contador"
          >
            {quitados.length}/{MAX_MODULOS_QUITADOS} sin incluir
          </span>
          <button
            type="button"
            onClick={() => !disabled && onChange([])}
            disabled={disabled || quitados.length === 0}
            data-testid="modulos-reset"
            className={cn(
              "inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-0.5 transition-all duration-200 focus:outline-none",
              disabled || quitados.length === 0
                ? "text-[#b8bae0] cursor-not-allowed"
                : "text-[#24255b] bg-[#eaf1fb] hover:bg-[#dbe6f8]"
            )}
          >
            <RotateCcw className="w-3 h-3" /> Resetear
          </button>
        </div>
      </div>

      <div className="grid gap-2.5">
        {MODULOS.map((m) => {
          const quitado = quitados.includes(m.key);
          const Icono = MODULO_ICONOS[m.key];
          const bloqueado = disabled || (!quitado && lleno);
          return (
            <div
              key={m.key}
              data-testid={`modulo-${m.key}`}
              className={cn(
                "rounded-xl border px-3.5 py-3 flex items-center gap-3 transition-all duration-200",
                quitado
                  ? "border-dashed border-[#e6e7f0] bg-[#f4f5fa] opacity-70"
                  : "border-[#e6e7f0] bg-white"
              )}
            >
              <div
                className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center"
                style={
                  quitado
                    ? { backgroundColor: "#eff0f6", color: "#b8bae0" }
                    : { backgroundColor: "#eaf1fb", color: INDIGO }
                }
              >
                <Icono className="w-4 h-4" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={cn("text-sm font-semibold", quitado ? "text-[#6c6d85]" : "text-[#24255b]")}>
                    {m.label}
                  </span>
                  {m.ilimitado && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: "#ecf8ee", color: "#3d9a4d" }}
                    >
                      Ilimitado
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#6c6d85] leading-tight mt-0.5">{m.detalle}</p>
              </div>

              <button
                type="button"
                onClick={() => toggle(m.key)}
                disabled={bloqueado}
                data-testid={`modulo-${m.key}-toggle`}
                className={cn(
                  "shrink-0 text-xs font-semibold rounded-lg px-3 py-1.5 flex items-center gap-1 transition-all duration-200 focus:outline-none",
                  quitado
                    ? "bg-[#24255b] text-white hover:bg-[#1a1b45]"
                    : "border border-[#ffc0c7] text-[#ec4456] bg-[#fff0f1] hover:bg-[#ffe3e6]",
                  bloqueado && "opacity-40 cursor-not-allowed"
                )}
              >
                {quitado ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> Incluir
                  </>
                ) : (
                  <>
                    <X className="w-3.5 h-3.5" /> Quitar
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <NotaMini>
        El plan incluye los 4 pilares. Puedes dejar de incluir hasta {MAX_MODULOS_QUITADOS}{" "}
        módulos para ajustar el alcance y el precio; los cambios se reflejan al instante.
      </NotaMini>
    </div>
  );
};

/* ---------- Notas explicativas ---------- */

const RoiAhorro = ({ config, r }) => {
  const a = calcularAhorro(config, r);

  if (!a.hayServicio) {
    return (
      <div
        className="rounded-2xl p-6 text-white"
        style={{ backgroundColor: INDIGO, boxShadow: "0 8px 30px rgba(36,37,91,.18)" }}
        data-testid="roi-retirada"
      >
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-4 h-4" style={{ color: GOLD }} />
          <p className="text-xs font-bold uppercase tracking-wider text-[#ff8a95]">
            El beneficio sigue entregando valor
          </p>
        </div>
        <h3 className="text-lg font-bold text-white">Un beneficio tangible para los estudiantes</h3>
        <p className="text-xs text-[#cdcfe8] mt-1 leading-relaxed">
          Aun sin los servicios ilimitados de telepsicología y telenutrición, el plan mantiene
          beneficios tangibles para los estudiantes, incluidos en todas las configuraciones:
        </p>
        <div className="mt-4 grid gap-2.5">
          {[
            { i: Trophy, t: "Gamificación", d: "Retos, logros y rankings que motivan el uso diario." },
            { i: Sparkles, t: "Premios e incentivos", d: "Recompensas por cumplir hábitos y participar." },
            { i: PiggyBank, t: "Descuentos y beneficios", d: "Acceso a una red de descuentos para la comunidad." },
          ].map((b) => (
            <div key={b.t} className="rounded-xl bg-white/10 px-4 py-3 flex items-start gap-3">
              <b.i className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
              <div>
                <p className="text-xs font-bold text-white">{b.t}</p>
                <p className="text-[11px] text-[#cdcfe8] leading-snug mt-0.5">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#b8bae0] mt-3 leading-relaxed">
          Activa la telepsicología o la telenutrición para ver, además, cuánto ahorran los
          estudiantes con los servicios ilimitados.
        </p>
      </div>
    );
  }

  const tituloServicios = a.servicios.map((s) => s.nombre.replace(" ilimitada", "")).join(" + ");
  const veces = a.inversionMensual > 0 ? a.ahorroMensual / a.inversionMensual : 0;
  const stat = Number(config.statNoUsa) || 0;

  return (
    <>
    <div
      className="rounded-2xl p-6 text-white"
      style={{ backgroundColor: INDIGO, boxShadow: "0 8px 30px rgba(36,37,91,.18)" }}
      data-testid="roi-ahorro"
    >
      <div className="flex items-center gap-2 mb-1">
        <PiggyBank className="w-4 h-4" style={{ color: GOLD }} />
        <p className="text-xs font-bold uppercase tracking-wider text-[#ff8a95]">
          Valor del beneficio para los estudiantes
        </p>
      </div>
      <h3 className="text-lg font-bold text-white">Cuánto ahorran los estudiantes al mes</h3>
      <p className="text-xs text-[#cdcfe8] mt-1 leading-relaxed">
        {tituloServicios}: es lo que los estudiantes gastarían si contrataran estos servicios de
        bienestar por su cuenta.
      </p>

      {/* Cifra principal */}
      <div className="mt-5">
        <p className="text-xs text-[#b8bae0]">Valor en servicios que reciben al mes</p>
        <p
          className="font-extrabold tabular-nums tracking-tight text-3xl sm:text-4xl leading-none whitespace-nowrap mt-1"
          style={{ color: GOLD }}
          data-testid="roi-ahorro-mensual"
        >
          {dinero(a.ahorroMensual)}
        </p>
        <p className="text-xs text-[#cdcfe8] mt-1">
          Por cada <span className="font-bold text-white">$1</span> que invierte la universidad,
          los estudiantes reciben{" "}
          <span className="font-bold text-white">{dinero(veces)}</span> en servicios de bienestar.
        </p>
      </div>

      {/* Desglose por servicio */}
      <div className="mt-5 space-y-2.5">
        {a.servicios.map((s) => (
          <ServicioLinea key={s.nombre} s={s} usuarios={a.usuarios} />
        ))}
      </div>

      {/* Cómo se calcula */}
      <div className="mt-3 rounded-xl bg-white/10 px-4 py-3 flex items-start gap-2.5">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#b8bae0]" />
        <p className="text-[11px] leading-relaxed text-[#cdcfe8]">
          <span className="font-bold text-white">Cómo se calcula:</span> por cada servicio
          estimamos cuántos estudiantes lo usan × cuántas citas toman al año × el costo de mercado
          de una cita. El costo por cita ({dinero(a.costoRefCita)}) es el{" "}
          <span className="font-semibold text-white">valor promedio de una cita de psicología en
          Ecuador</span>. La suma es el valor total en servicios que reciben los estudiantes.
        </p>
      </div>

      {/* Comparación: costo universidad vs valor estudiantes */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-white/10 px-3 py-2.5">
          <p className="text-[11px] text-[#b8bae0]">Costo para la universidad</p>
          <p className="text-sm font-bold tabular-nums text-white mt-0.5 whitespace-nowrap">
            {dinero(a.inversionMensual)}
          </p>
        </div>
        <div className="rounded-xl bg-white/10 px-3 py-2.5">
          <p className="text-[11px] text-[#b8bae0]">Valor entregado por cada $1</p>
          <p
            className="text-sm font-bold tabular-nums mt-0.5 whitespace-nowrap"
            style={{ color: GOLD }}
            data-testid="roi-beneficio-neto"
          >
            {dinero(veces)} · {numeroDec(veces)}×
          </p>
        </div>
      </div>

      {/* Barrera del costo + beneficio tangible */}
      {stat > 0 && (
        <div
          className="mt-4 rounded-xl p-4"
          style={{ backgroundColor: "rgba(255,85,102,.16)" }}
          data-testid="estadistica-uso"
        >
          <div className="flex items-start gap-3">
            <UserX className="w-5 h-5 shrink-0 mt-0.5 text-[#ff8a95]" />
            <p className="text-xs leading-relaxed text-white">
              <span
                className="font-extrabold text-2xl align-middle"
                style={{ color: "#ff8a95" }}
                data-testid="estadistica-uso-valor"
              >
                {numeroDec(stat)}%
              </span>{" "}
              de los estudiantes <span className="font-bold">no usa</span> estos servicios{" "}
              <span className="font-bold">por lo que cuestan</span>… y sí los usaría si fueran
              gratuitos.
            </p>
          </div>
          <p className="text-xs leading-relaxed text-[#cdcfe8] mt-2.5">
            Al ofrecerlos sin costo, la universidad convierte ese ahorro en un{" "}
            <span className="font-extrabold text-white uppercase tracking-wide">
              beneficio tangible
            </span>{" "}
            que los estudiantes sí van a usar.
          </p>
        </div>
      )}

      <p className="text-[11px] text-[#b8bae0] mt-3 leading-relaxed">
        Por un costo institucional muy bajo, los estudiantes reciben un beneficio tangible.
        Considera solo la telesalud; los demás módulos, la gamificación y los premios suman aún
        más valor.
      </p>
    </div>

    {/* Recuperación con una sola cita */}
    <div
      className="rounded-2xl border border-[#f3e6b8] p-5"
      style={{ backgroundColor: "#fff8e6" }}
      data-testid="roi-una-cita"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#24255b]" />
        <p className="text-sm font-bold text-[#24255b]">
          El beneficio genera mucho más valor de lo que cuesta
        </p>
      </div>
      <p className="text-xs leading-relaxed text-[#3a3b52]">
        Dar acceso ilimitado a cada estudiante cuesta apenas{" "}
        <span className="font-bold text-[#24255b]">
          {dinero(a.costoLicenciaAnualUsuario)}
        </span>{" "}
        al año ({dinero(a.precioPromedioPorUsuario)} × 12 meses), mientras que{" "}
        <span className="font-bold text-[#24255b]">una sola cita cuesta {dinero(a.costoRefCita)}</span>{" "}
        en el mercado.
      </p>
      <p className="text-xs leading-relaxed text-[#3a3b52] mt-2">
        Con usar el servicio{" "}
        <span className="font-bold text-[#24255b]">1 vez al año</span>, el estudiante ya recibe{" "}
        <span
          className="font-extrabold text-[#3d9a4d]"
          data-testid="roi-una-cita-veces"
        >
          {numeroDec(a.vecesRecuperaUnaCita)}×
        </span>{" "}
        más valor de lo que costó su licencia anual.
      </p>
    </div>
    </>
  );
};

const PuntoEquilibrio = ({ config, r }) => {
  const a = calcularAhorro(config, r);
  if (!a.hayServicio) return null;
  return (
    <div
      className="rounded-2xl border border-[#cddcf3] p-6"
      style={{ backgroundColor: "#eaf1fb", boxShadow: "0 8px 30px rgba(36,37,91,.06)" }}
      data-testid="roi-equilibrio"
    >
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-4 h-4 text-[#2f5797]" />
        <h3 className="text-base font-bold text-[#24255b]">
          El beneficio supera ampliamente el costo
        </h3>
      </div>
      <p className="text-xs leading-relaxed text-[#3a3b52]">
        Con lo que invierte la universidad al mes ({" "}
        <span className="font-bold text-[#24255b]">{dinero(a.inversionMensual)}</span>) y un costo
        de <span className="font-bold text-[#24255b]">{dinero(a.costoRefCita)}</span> por cita,
        bastarían{" "}
        <span className="font-extrabold text-[#2f5797]" data-testid="roi-equilibrio-citas">
          {numero(Math.ceil(a.citasParaPagar))} citas al mes
        </span>{" "}
        de los estudiantes para igualar toda la inversión — y el valor entregado la supera
        ampliamente.
      </p>
      <p className="text-[11px] leading-relaxed text-[#6c6d85] mt-2 italic">
        * Los {dinero(a.costoRefCita)} corresponden al valor promedio de una cita de psicología
        en Ecuador con un profesional con experiencia y credenciales acreditadas.
      </p>
      <div className="mt-4 rounded-xl bg-white/70 border border-[#cddcf3] p-4">
        <p className="text-sm sm:text-base leading-snug text-[#24255b] font-medium">
          Con que solo el{" "}
          <span
            className="font-extrabold text-[#3d9a4d] text-2xl sm:text-3xl align-middle"
            data-testid="roi-equilibrio-tasa"
          >
            {numeroDec(a.tasaUsuariosPagar)}%
          </span>{" "}
          de los {numero(a.usuarios)} estudiantes tome{" "}
          <span className="font-bold">1 cita al mes</span>, ya reciben en servicios lo mismo que
          cuesta todo el beneficio.
        </p>
      </div>
    </div>
  );
};

const ServicioLinea = ({ s, usuarios }) => (
  <div className="rounded-xl bg-white/10 px-4 py-3" data-testid="roi-servicio">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-bold text-white">{s.nombre}</p>
      <p className="text-sm font-extrabold tabular-nums whitespace-nowrap" style={{ color: GOLD }}>
        +{dinero(s.ahorro)}
      </p>
    </div>
    <p className="text-[11px] leading-relaxed text-[#cdcfe8] mt-1">
      ~{numeroDec(s.adopcion)}% de {numero(usuarios)} usuarios ={" "}
      {numero(Math.round(s.personas))} personas · {numeroDec(s.citasAno)} citas/año ·{" "}
      {dinero(s.costoCita)} c/u
    </p>
  </div>
);

const NotaGamificacion = () => (
  <div
    className="rounded-xl border border-[#f3e6b8] px-4 py-3 flex items-start gap-3"
    style={{ backgroundColor: "#fff8e6" }}
    data-testid="nota-gamificacion"
  >
    <div
      className="mt-0.5 w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-white"
      style={{ backgroundColor: GOLD, color: INDIGO }}
    >
      <Trophy className="w-4 h-4" />
    </div>
    <p className="text-xs leading-relaxed text-[#3a3b52]">
      <span className="font-bold text-[#24255b]">Todos los planes incluyen gamificación,
      beneficios y premios</span> sin costo adicional, en cualquier configuración que elijas.
    </p>
  </div>
);

const NotaDesarrollo = () => (
  <div
    className="rounded-xl border border-[#f3e6b8] px-4 py-3 flex items-start gap-3"
    style={{ backgroundColor: "#fff8e6" }}
    data-testid="nota-desarrollo"
  >
    <div
      className="mt-0.5 w-8 h-8 shrink-0 rounded-lg flex items-center justify-center"
      style={{ backgroundColor: GOLD, color: INDIGO }}
    >
      <Code2 className="w-4 h-4" />
    </div>
    <p className="text-xs leading-relaxed text-[#3a3b52]">
      <span className="font-bold text-[#24255b]">Incluye 25 horas de desarrollo</span> para
      cualquier integración con tus sistemas o programas, desarrollo de nuevas funcionalidades
      y más.
    </p>
  </div>
);

const NotaMinimo = () => (  <div
    className="rounded-xl border border-[#cddcf3] px-4 py-3 flex items-start gap-3"
    style={{ backgroundColor: "#eaf1fb" }}
    data-testid="nota-minimo"
  >
    <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2f5797]" />
    <p className="text-xs leading-relaxed text-[#2f5797]">
      Se requiere un <span className="font-bold">mínimo de 2 funcionalidades activas</span> para
      contratar el plan.
    </p>
  </div>
);

const NotaEscala = () => (
  <div
    className="rounded-xl border border-[#cddcf3] px-4 py-3 flex items-start gap-3"
    style={{ backgroundColor: "#eaf1fb" }}
    data-testid="nota-escala"
  >
    <Layers className="w-4 h-4 mt-0.5 shrink-0 text-[#2f5797]" />
    <p className="text-xs leading-relaxed text-[#2f5797]">
      El precio por usuario de los rangos de estudiantes es posible porque{" "}
      <span className="font-bold">unificamos este contrato con el de RRHH en un solo
      proyecto</span>, aprovechando las economías de escala.
    </p>
  </div>
);

const NotaEscalonado = () => (
  <div
    className="mt-3 rounded-xl border border-[#cddcf3] px-4 py-3 flex items-start gap-3"
    style={{ backgroundColor: "#eaf1fb" }}
    data-testid="nota-escalonado"
  >
    <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2f5797]" />
    <p className="text-xs leading-relaxed text-[#2f5797]">
      El precio es <span className="font-bold">escalonado</span>: la tarifa de cada rango
      aplica <span className="font-bold">únicamente a los usuarios de ese rango</span>, no a
      todos. Al pasar al siguiente rango, su tarifa afecta solo a los usuarios adicionales por
      encima del rango anterior.
    </p>
  </div>
);

const NotaPromedio = ({ total, usuarios, promedio }) => (
  <div
    className="rounded-xl border border-[#cddcf3] px-4 py-3 flex items-start gap-3"
    style={{ backgroundColor: "#eaf1fb" }}
    data-testid="nota-promedio"
  >
    <Calculator className="w-4 h-4 mt-0.5 shrink-0 text-[#2f5797]" />
    <div className="text-xs leading-relaxed text-[#2f5797]">
      <p>
        <span className="font-bold">¿Cómo se calcula el precio promedio por usuario?</span>{" "}
        Se toma el total mensual y se divide entre todos los usuarios (RRHH + estudiantes).
      </p>
      <p className="mt-1">
        Ejemplo con tu selección actual:{" "}
        <span className="font-semibold text-[#24255b]">{dinero(total)}</span> ÷{" "}
        <span className="font-semibold text-[#24255b]">{numero(usuarios)} usuarios</span> ={" "}
        <span className="font-bold text-[#24255b]">{dinero(promedio)}</span> por usuario al mes.
      </p>
    </div>
  </div>
);

const NotaMini = ({ children }) => (
  <p className="text-[11px] leading-relaxed text-[#6c6d85] pl-0.5">{children}</p>
);

/* ---------- Tarjetas y filas ---------- */

const SummaryCard = ({ icon, label, value, highlight, nota, testId }) => (
  <div
    data-testid={testId}
    className={
      highlight
        ? "rounded-2xl p-4 text-white"
        : "rounded-2xl p-4 border border-[#e6e7f0] bg-white"
    }
    style={
      highlight
        ? { backgroundColor: INDIGO, boxShadow: "0 8px 30px rgba(36,37,91,.18)" }
        : { boxShadow: "0 8px 30px rgba(36,37,91,.06)" }
    }
  >
    <div
      className="inline-flex items-center justify-center w-9 h-9 rounded-lg mb-3"
      style={
        highlight
          ? { backgroundColor: "rgba(255,255,255,.14)", color: GOLD }
          : { backgroundColor: "#eaf1fb", color: INDIGO }
      }
    >
      {icon}
    </div>
    <p className={"text-xs font-medium " + (highlight ? "text-[#cdcfe8]" : "text-[#6c6d85]")}>
      {label}
    </p>
    <p
      className="mt-1 font-extrabold tabular-nums tracking-tight text-lg sm:text-xl leading-tight whitespace-nowrap"
      style={{ color: highlight ? GOLD : INDIGO }}
    >
      {value}
    </p>
    {nota && (
      <p className={"text-[11px] mt-2 leading-snug " + (highlight ? "text-[#b8bae0]" : "text-[#6c6d85]")}>
        {nota}
      </p>
    )}
  </div>
);

const TramoRow = ({ nombre, rango, usuarios, precio, subtotal, atenuado }) => (
  <tr className={atenuado ? "text-[#b8bae0]" : "text-[#3a3b52]"}>
    <td className="px-6 py-3.5">
      <span className="font-semibold block">{nombre}</span>
      {rango && (
        <span className={"text-[11px] " + (atenuado ? "text-[#b8bae0]" : "text-[#6c6d85]")}>
          {rango}
        </span>
      )}
    </td>
    <td className="px-4 py-3.5 text-right tabular-nums align-top pt-3.5">{numero(usuarios)}</td>
    <td className="px-4 py-3.5 text-right tabular-nums align-top pt-3.5">{dinero(precio)}</td>
    <td className="px-6 py-3.5 text-right tabular-nums font-semibold align-top pt-3.5">{dinero(subtotal)}</td>
  </tr>
);

const BarraDesglose = ({ rrhh, estudiantes, total }) => {
  const pRRHH = total > 0 ? (rrhh / total) * 100 : 100;
  const pEst = total > 0 ? (estudiantes / total) * 100 : 0;
  return (
    <div
      className="w-full h-4 rounded-full overflow-hidden flex bg-[#eff0f6]"
      data-testid="barra-desglose"
    >
      <div className="h-full transition-all duration-500" style={{ width: `${pRRHH}%`, backgroundColor: INDIGO }} />
      <div className="h-full transition-all duration-500" style={{ width: `${pEst}%`, backgroundColor: BLUE }} />
    </div>
  );
};

const BreakdownCard = ({ color, label, usuarios, monto, testId, icon }) => (
  <div className="rounded-xl border border-[#e6e7f0] p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-sm font-semibold text-[#3a3b52] flex items-center gap-1">
        {icon}
        {label}
      </span>
    </div>
    <p className="text-xl font-extrabold tabular-nums text-[#24255b]">{dinero(monto)}</p>
    <p className="text-xs text-[#6c6d85] mt-0.5">{numero(usuarios)} usuarios · al mes</p>
  </div>
);
